// Photo mode (ui-map-interface.md §13.1, R1623; round 077 cards 06 and 07).
//
// OPTIONS · PHOTO, or shift with p on the map, hides the whole chrome and
// leaves one slim bar: ✕ LEAVE, the zoom slider with its reading, the MARK
// switch, the GRID switch and ◉ SAVE PNG.
//
// - **The chrome goes through the veil.** The chrome veil of the season
//   summary (`ui/seasonover.ts`, R1519) has photo mode as its second
//   cause, so one mechanism hides the top bar, the dock or the sheet, the
//   chat, the notices, the selection panel and the minimap. The map's own
//   selection marks go with them: the page's `onEnter` takes the bracket
//   off the fleet layer and gives it back on `onLeave`.
// - **The map takes no order** (`fast/input.ts` reads `on`). A drag pans,
//   the wheel and a pinch zoom, and the world keeps running. A finger's tap
//   on the map hides the bar, and a second tap brings it back.
// - **The zoom runs past the game's range** at both ends (R1624): the
//   camera takes `PHOTO_MIN_ZOOM` to `PHOTO_MAX_ZOOM` while photo mode is
//   on, and the game's range again when it ends. The slider reads the zoom
//   on a log scale.
// - **SAVE PNG saves the map as the glass shows it** (card 07): a copy of
//   the next frame at the resolution of the screen, with no bar, and the
//   mark in its lower left unless MARK is off. A telephone hands the file
//   to the system share sheet where the browser takes a file; every other
//   browser downloads it, and the line SAVED names the file.
//
// The rules are pure functions below the class, and the class holds the
// DOM. `deps` is everything the class touches outside itself, so a test
// drives it with plain objects.

import {
  ACCENT_CSS, PALETTE_TOKENS, PHOTO_MARK, PHOTO_MAX_ZOOM, PHOTO_MIN_ZOOM,
  PHOTO_SAVED_MS,
} from '../gen/presentation';
import { photo as S } from '../gen/strings';
import { esc } from './esc';
import { hhmm } from '../locale/clock';
import { PressRoute } from './pressroute';
import { coarsePointer } from './pointer';
import { drawSign } from './sign';
import { packChannels, rgba } from '../render/color';
import { clamp01 } from '../render/math';

// --- the zoom ---------------------------------------------------------------

/** A zoom held inside the photo range. */
export function photoZoom(zoom: number): number {
  return Math.min(PHOTO_MAX_ZOOM, Math.max(PHOTO_MIN_ZOOM, zoom));
}

/** The slider position, 0 to 1, of a zoom: a log scale over the range. */
export function sliderOf(zoom: number): number {
  return Math.log(photoZoom(zoom) / PHOTO_MIN_ZOOM) / Math.log(PHOTO_MAX_ZOOM / PHOTO_MIN_ZOOM);
}

/** The zoom of a slider position. */
export function zoomOf(t: number): number {
  const u = clamp01(t);
  return PHOTO_MIN_ZOOM * (PHOTO_MAX_ZOOM / PHOTO_MIN_ZOOM) ** u;
}

/** The reading beside the slider: one decimal from 1 up, two under it, and
 *  no trailing zero ("2.4×", "0.4×", "0.12×"). */
export function zoomReading(zoom: number): string {
  const n = zoom >= 1 ? zoom.toFixed(1) : zoom.toFixed(2).replace(/0$/, '');
  return S.zoom_value({ zoom: n });
}

// --- the file -----------------------------------------------------------------

/** What the mark and the file name state of the moment. */
export interface PhotoFacts {
  world: string;
  season: number;
  day: number;
  /** The world's time of day, in game seconds. */
  clockSec: number;
}

/** The world name as a file name part: lowercase letters and digits joined
 *  by dashes. */
export function worldSlug(name: string): string {
  const s = name.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s === '' ? 'world' : s;
}

/** `aquaphobia-<world>-s<season>-d<day>-<hhmm>.png`, the time on the world's
 *  own clock (R1625). */
export function photoFileName(f: PhotoFacts): string {
  const hm = hhmm(f.clockSec).replace(':', '');
  return `${S.brand.toLowerCase()}-${worldSlug(f.world)}-s${f.season}-d${f.day}-${hm}.png`;
}

// --- the mark -----------------------------------------------------------------

/** The scale of the mark on a picture: its long side over the base (R1625). */
export function markScale(w: number, h: number): number {
  return Math.max(w, h) / PHOTO_MARK.basePx;
}

/** Sets the letter spacing where the browser's canvas has it. */
function track(c: CanvasRenderingContext2D, px: number): void {
  const t = c as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in t) t.letterSpacing = `${px}px`;
}

/** The deep ink of the shade, as `r, g, b`. */
const DEEP = packChannels(PALETTE_TOKENS['--deep-rgb']);
const TEXT = PALETTE_TOKENS['--text'];

/**
 * Draws the mark in the lower left of a picture `w` by `h` (card 07): the
 * soft shade, the sign, the word and the line of the world, the season and
 * the day. `font` is the chrome's font family.
 */
export function drawMark(
  c: CanvasRenderingContext2D, w: number, h: number,
  words: { brand: string; line: string }, font: string,
): void {
  const m = PHOTO_MARK;
  const k = markScale(w, h);
  const x = m.padPx * k;
  const y = h - (m.padPx + m.blockPx) * k;
  c.save();
  const r = m.shadePx * k;
  const g = c.createRadialGradient(0, h, 0, 0, h, r);
  g.addColorStop(0, rgba(DEEP, m.shadeAlpha));
  g.addColorStop(1, rgba(DEEP, 0));
  c.fillStyle = g;
  c.fillRect(0, h - r, r, r);

  // The Aquaphobia sign with no plate (brand.md §1, R1644): the shade is
  // its dark ground.
  drawSign(c, x, y, m.signPx * k, { plate: false });

  c.textAlign = 'left';
  c.textBaseline = 'middle';
  track(c, m.titleTrack * k);
  c.font = `500 ${m.titlePx * k}px ${font}`;
  c.globalAlpha = m.titleAlpha;
  c.fillStyle = ACCENT_CSS;
  c.fillText(words.brand, x + m.textPx * k, y + m.titleY * k);
  track(c, m.lineTrack * k);
  c.font = `400 ${m.linePx * k}px ${font}`;
  c.globalAlpha = m.lineAlpha;
  c.fillStyle = TEXT;
  c.fillText(words.line, x + m.textPx * k, y + m.lineY * k);
  track(c, 0);
  c.restore();
}

/** The words of the mark for the facts of the moment. The mark names the
 *  world, the season and the day, and no fleet and no player. */
export function markWords(f: PhotoFacts): { brand: string; line: string } {
  return { brand: S.brand, line: S.mark_line({ world: f.world.toUpperCase(), season: f.season, day: f.day }) };
}

// --- the delivery -------------------------------------------------------------

/** How a saved picture leaves the page. A test passes plain functions. */
export interface DeliverDeps {
  /** True on a finger-driven pointer, where the system share sheet lives. */
  coarse: boolean;
  /** The system share sheet, or null where the browser has none. */
  share: ((data: ShareData) => Promise<void>) | null;
  /** Whether the share sheet takes this data, or null where it cannot say. */
  canShare: ((data: ShareData) => boolean) | null;
  /** A file of the blob, or null where the browser makes none. */
  file: (blob: Blob, name: string) => File | null;
  /** Downloads the blob under the name. */
  download: (blob: Blob, name: string) => void;
}

/** What happened to a saved picture. `dismissed` is a share sheet the
 *  player closed: the picture went nowhere, and nothing more is said. */
export type Delivery = 'shared' | 'downloaded' | 'dismissed';

/**
 * Hands the picture on: to the share sheet on a telephone whose browser
 * shares a file, else to a download. A share sheet that fails for any
 * reason but the player's own dismissal falls back to the download.
 */
export async function deliverPhoto(blob: Blob, name: string, d: DeliverDeps): Promise<Delivery> {
  if (d.coarse && d.share && d.canShare) {
    const file = d.file(blob, name);
    const data: ShareData | null = file ? { files: [file] } : null;
    if (data && d.canShare(data)) {
      try {
        await d.share(data);
        return 'shared';
      } catch (err) {
        if ((err as { name?: string } | null)?.name === 'AbortError') return 'dismissed';
      }
    }
  }
  d.download(blob, name);
  return 'downloaded';
}

/** The real browser. It is read at the press, so a page that rotated into
 *  a touch lane gets the right answer. */
export function browserDelivery(): DeliverDeps {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  return {
    coarse: coarsePointer(),
    share: nav && typeof nav.share === 'function' ? (data) => nav.share(data) : null,
    canShare: nav && typeof nav.canShare === 'function' ? (data) => nav.canShare(data) : null,
    file: (blob, name) => (typeof File === 'function' ? new File([blob], name, { type: 'image/png' }) : null),
    download: (blob, name) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // The click starts the download at once; the address is let go
      // after the browser has read it.
      setTimeout(() => URL.revokeObjectURL(url), PHOTO_SAVED_MS);
    },
  };
}

// --- the mode -----------------------------------------------------------------

/** What photo mode needs of the page. */
export interface PhotoDeps {
  /** The chrome root `#ui`; the bar and its two lines join it. */
  shell: HTMLElement;
  camera: {
    readonly zoom: number;
    zoomTo(zoom: number): void;
    setZoomRange(min: number, max: number, centreSmall?: boolean): void;
    resetZoomRange(): void;
  };
  /** The hex lines (the `hex_borders` view toggle, R456). */
  grid: { get(): boolean; set(on: boolean): void };
  /** The chrome veil (R1519): photo mode is its second cause. */
  veil: { setPhoto(on: boolean): void };
  /** A copy of the next frame of the map, or null. */
  capture: () => Promise<HTMLCanvasElement | null>;
  /** The canvas as a PNG. */
  png: (canvas: HTMLCanvasElement) => Promise<Blob | null>;
  facts: () => PhotoFacts;
  /** The page's own work on the way in and out: the selection marks, an
   *  armed pick, the tooltip, an open card. */
  onEnter?: () => void;
  onLeave?: () => void;
  deliver?: () => DeliverDeps;
  /** The chrome's font family, for the mark. */
  font?: () => string;
}

export class PhotoMode {
  private onNow = false;
  /** The MARK switch. It starts on, and a session keeps the last pick. */
  private markOn = true;
  /** The hex lines before photo mode, given back when it ends. */
  private gridWas = false;
  private barAway = false;
  private saving = false;
  private savedTimer = 0;
  private lastReading = '';
  /** The zoom the reading and the slider were last synced to. */
  private lastZoom = NaN;
  readonly bar: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly saved: HTMLElement;
  private readonly slider: HTMLInputElement;
  private readonly reading: HTMLElement;
  private readonly markBtn: HTMLButtonElement;
  private readonly gridBtn: HTMLButtonElement;

  constructor(private readonly d: PhotoDeps) {
    const bar = document.createElement('div');
    bar.id = 'photobar';
    bar.className = 'panel';
    bar.innerHTML = `<button type="button" data-ph="leave" title="${esc(S.leave_title)}">✕<span class="lw"> ${esc(S.leave)}</span></button>`
      + `<span class="zr">−<input type="range" min="0" max="1" step="any" aria-label="${esc(S.zoom)}">+`
      + `<span class="zv"></span></span>`
      + `<button type="button" class="sw" data-ph="mark" title="${esc(S.mark_title)}"><i></i>${esc(S.mark)}</button>`
      + `<button type="button" class="sw" data-ph="grid" title="${esc(S.grid_title)}"><i></i>${esc(S.grid)}</button>`
      + `<button type="button" class="primary" data-ph="save">◉ ${esc(S.save)}</button>`;
    this.bar = bar;
    this.slider = bar.querySelector('input') as HTMLInputElement;
    this.reading = bar.querySelector('.zv') as HTMLElement;
    this.markBtn = bar.querySelector('[data-ph="mark"]') as HTMLButtonElement;
    this.gridBtn = bar.querySelector('[data-ph="grid"]') as HTMLButtonElement;
    // Fixed chrome over the map: a button acts on the press, and Enter and
    // Space act through their click (§16.8, R1206, R1569). The bar stands
    // for the life of the page, so the router is never taken down.
    new PressRoute(bar, {
      control: 'button[data-ph]', scrolls: () => false,
      act: (btn) => this.press(btn.dataset.ph ?? ''),
    });
    this.slider.addEventListener('input', () => {
      this.d.camera.zoomTo(zoomOf(Number(this.slider.value)));
      this.sync();
    });
    this.hint = document.createElement('div');
    this.hint.id = 'photohint';
    this.hint.textContent = S.hint;
    this.saved = document.createElement('div');
    this.saved.id = 'photosaved';
    this.saved.className = 'panel';
    this.saved.setAttribute('role', 'status');
    d.shell.append(bar, this.hint, this.saved);
    this.paintSwitches();
  }

  /** True while photo mode is on. */
  get on(): boolean { return this.onNow; }

  /** True while the MARK switch is on. */
  get mark(): boolean { return this.markOn; }

  /** True while a tap has put the bar away. */
  get barHidden(): boolean { return this.barAway; }

  enter(): void {
    if (this.onNow) return;
    this.onNow = true;
    this.barAway = false;
    this.bar.classList.remove('away');
    this.gridWas = this.d.grid.get();
    this.d.camera.setZoomRange(PHOTO_MIN_ZOOM, PHOTO_MAX_ZOOM, true);
    this.d.veil.setPhoto(true);
    this.d.onEnter?.();
    this.lastReading = '';
    this.lastZoom = NaN;
    this.paintSwitches();
    this.sync();
  }

  leave(): void {
    if (!this.onNow) return;
    this.onNow = false;
    this.barAway = false;
    this.bar.classList.remove('away');
    this.d.grid.set(this.gridWas);
    this.d.camera.resetZoomRange();
    this.d.veil.setPhoto(false);
    this.hideSaved();
    this.d.onLeave?.();
  }

  toggle(): void {
    if (this.onNow) this.leave();
    else this.enter();
  }

  /** A finger's tap on the map: the bar goes, and the next tap brings it back. */
  tap(): void {
    if (!this.onNow) return;
    this.barAway = !this.barAway;
    this.bar.classList.toggle('away', this.barAway);
  }

  /** Follows the zoom: the slider and the reading. The page calls it each
   *  frame; a frame whose zoom did not move writes no string. The slider
   *  follows every move of the zoom, a pinch inside one reading step too
   *  (R3021), and the reading text changes only when its words change. */
  sync(): void {
    if (!this.onNow) return;
    const z = this.d.camera.zoom;
    if (z === this.lastZoom) return;
    this.lastZoom = z;
    const t = sliderOf(z);
    if (Math.abs(Number(this.slider.value) - t) > 0.002) this.slider.value = String(t);
    const text = zoomReading(z);
    if (text === this.lastReading) return;
    this.lastReading = text;
    this.reading.textContent = text;
  }

  /** One press of the bar. */
  press(what: string): void {
    switch (what) {
      case 'leave': this.leave(); break;
      case 'mark': this.markOn = !this.markOn; this.paintSwitches(); break;
      case 'grid': this.d.grid.set(!this.d.grid.get()); this.paintSwitches(); break;
      case 'save': void this.save(); break;
    }
  }

  /**
   * Saves the map (card 07): the next frame, the mark unless MARK is off,
   * a PNG, and the share sheet or a download. It answers what happened.
   */
  async save(): Promise<Delivery | 'failed' | 'busy'> {
    if (this.saving) return 'busy';
    this.saving = true;
    try {
      const shot = await this.d.capture();
      if (!shot) { this.flash(S.save_failed, '', true); return 'failed'; }
      const facts = this.d.facts();
      if (this.markOn) {
        const c = shot.getContext('2d');
        if (c) drawMark(c, shot.width, shot.height, markWords(facts), this.d.font?.() ?? 'monospace');
      }
      const blob = await this.d.png(shot);
      if (!blob) { this.flash(S.save_failed, '', true); return 'failed'; }
      const name = photoFileName(facts);
      // The picture is made. A share sheet can stand open for as long as
      // the player likes, so SAVE PNG is free again from here.
      this.saving = false;
      const how = await deliverPhoto(blob, name, (this.d.deliver ?? browserDelivery)());
      if (how === 'downloaded') this.flash(S.saved, name, false);
      return how;
    } catch {
      this.flash(S.save_failed, '', true);
      return 'failed';
    } finally {
      this.saving = false;
    }
  }

  private paintSwitches(): void {
    const grid = this.d.grid.get();
    this.markBtn.classList.toggle('on', this.markOn);
    this.markBtn.setAttribute('aria-pressed', String(this.markOn));
    this.gridBtn.classList.toggle('on', grid);
    this.gridBtn.setAttribute('aria-pressed', String(grid));
  }

  /** The line SAVED · <file>, or a failure, for `PHOTO_SAVED_MS`. */
  private flash(head: string, name: string, bad: boolean): void {
    this.saved.innerHTML = `<b>${esc(head)}</b>${name ? ` · ${esc(name)}` : ''}`;
    this.saved.classList.toggle('bad', bad);
    this.saved.classList.add('on');
    window.clearTimeout(this.savedTimer);
    this.savedTimer = window.setTimeout(() => this.hideSaved(), PHOTO_SAVED_MS);
  }

  private hideSaved(): void {
    window.clearTimeout(this.savedTimer);
    this.savedTimer = 0;
    this.saved.classList.remove('on');
  }
}
